import { useQuery } from '@tanstack/react-query';

import type { Vacancy } from 'clients/AutoCompleteClient/types';

import { autoCompleteClient } from 'clients/AutoCompleteClient/AutoCompleteClient';
import { reachoutClient } from 'clients/ReachoutClient/ReachoutClient';
import { formatObjectToReadableText } from 'helpers/formatObjectToReadableText';
import { QueryKey } from 'types/query';
import { sanitizeEmailHtml } from 'utils/sanitizeEmailHtml';

const descriptionText = (value: string) =>
  new DOMParser().parseFromString(sanitizeEmailHtml(value).replace(/<\/(p|li)>|<br\s*\/?\s*>/gi, '$&\n'), 'text/html')
    .body.textContent || '';

const firstDescription = (...values: (string | null | undefined)[]) =>
  values.find((value) => value && descriptionText(value).trim()) || '';

export const useVacancyPreview = (vacancy: Vacancy, enabled: boolean) => {
  const isAts = vacancy.type === 'ATS' || vacancy.type === 'ATS_RAW';
  const atsId = isAts ? vacancy.job_ats_id : undefined;
  const ats = useQuery(
    [QueryKey.useAtsVacancyFullQuery, atsId],
    async () => {
      const { data } = await autoCompleteClient.fullAtsVacancy(atsId!);
      return data;
    },
    { enabled: enabled && !!atsId, staleTime: 5 * 60 * 1000, retry: false },
  );

  // Match the original-description tab: include mapped fields when the ATS uses custom fields.
  const mappedDescription = ats.data?.ai_details
    ? formatObjectToReadableText({
        ...ats.data.ai_details,
        complementary_description: ats.data.description ? descriptionText(ats.data.description) : null,
      })
    : '';
  const atsDescription = firstDescription(mappedDescription, ats.data?.description);
  const useSavedDescription = !atsDescription && (!atsId || ats.isError || ats.isSuccess);
  const saved = useQuery(
    [QueryKey.useGetJobImproved, vacancy.db_id && { db_id: vacancy.db_id }],
    async () => {
      const { data } = await reachoutClient.getJobImproved(vacancy.db_id!);
      return data;
    },
    {
      enabled: enabled && useSavedDescription && !!vacancy.db_id,
      staleTime: 5 * 60 * 1000,
      retry: false,
    },
  );

  return {
    title: ats.data?.title || vacancy.name,
    location: atsDescription
      ? [ats.data?.location?.city, ats.data?.location?.country].filter(Boolean).join(', ')
      : saved.data?.employment_requirements?.address,
    description:
      atsDescription ||
      (useSavedDescription
        ? firstDescription(
            saved.data?.origin_text,
            saved.data?.job_preview,
            saved.data?.position_overview?.description,
            saved.data?.summary,
          )
        : ''),
    isPlainText: !!mappedDescription && atsDescription === mappedDescription,
    isLoading: (!!atsId && ats.isLoading) || (useSavedDescription && !!vacancy.db_id && saved.isLoading),
    isError: useSavedDescription && (vacancy.db_id ? saved.isError : ats.isError),
  };
};
